import type { ConnectionStatus } from '@/bridge/CommandBridge'
import {
    type CommandBridge,
    type CommandHandler,
    type CommandPayload,
} from '@/bridge/CommandBridge'
import { RtcBridge, type RtcBridgeOptions } from '@/bridge/rtc/RtcBridge'
import { getCustomer } from '@/customer/customer'
import { sha256 } from '@/util/crypto'
import { getAuth } from '@/auth/authRef'
import type { LogTarget, Unsubscribe } from '@amazon/vinyl-util'
import { createLogPrefix, globalRef, logDebug, logWarn } from '@amazon/vinyl-util'
import { type ObservableValue } from '@amazon/vinyl-observable'

const CASTING_HOST = 'ws://bullfrog-rendezvous-env.eba-3uhjvufa.us-east-1.elasticbeanstalk.com:80/'
// const CASTING_HOST = 'ws://localhost:8080/'

export interface CastingBridge extends CommandBridge {
    connect(): void
    disconnect(): void
}

export class CastingBridgeImpl implements CastingBridge, LogTarget {
    logPrefix = createLogPrefix('CastingBridgeImpl')
    private readonly rtcBridge = new RtcBridge()

    private signInSub: Unsubscribe | null = null

    get connectionStatus(): ObservableValue<ConnectionStatus> {
        return this.rtcBridge.connectionStatus
    }

    connect(): void {
        if (!getAuth().isSignedIn()) {
            logWarn(this, 'connect failed, not signed in')
            return
        }
        logDebug(this, 'connecting...')
        const bridge = this.rtcBridge

        void (async () => {
            const connectOptions: RtcBridgeOptions = {
                roomId: await sha256((await getCustomer()).customerHome.customerId),
                host: CASTING_HOST,
            }
            logDebug(this, 'connecting rtc bridge')
            bridge.connect(connectOptions)
        })()
    }

    disconnect(): void {
        logDebug(this, 'disconnect')
        this.rtcBridge.disconnect()
        this.signInSub?.()
        this.signInSub = null
    }

    canSend(): boolean {
        return this.rtcBridge.canSend()
    }

    listen(handler: CommandHandler): Unsubscribe {
        return this.rtcBridge.listen(handler)
    }

    send(command: CommandPayload): void {
        this.rtcBridge.send(command)
    }

    dispose() {
        this.rtcBridge.dispose()
    }
}

export const castingBridgeRef = globalRef<CastingBridge>(() => {
    return new CastingBridgeImpl()
})

export function getCastingBridge(): CastingBridge {
    return castingBridgeRef.value
}
